import { ChartCard, ChartValueTable } from '@/components/molecules/org-admin/charts/ChartCard';
import { RankedBarChart, type RankedBarDatum } from '@/components/molecules/org-admin/charts/RankedBarChart';
import type { OrgOverview } from '@/services/org-admin/overview';
import { Section } from '@/components/molecules/org-admin/OrgAdminSection';
import { formatCount, formatPercent, formatRatio } from './overviewLayout';

/**
 * "How are they using nCoder?" — repeat use, and how much work one prompt turns into.
 *
 * Two cards left this section:
 *
 *   - **Services** (cost per run) moved to the money band as "Most expensive features". It was a
 *     cost ranking sitting under a usage heading, and the money band is where a cost preview belongs.
 *   - **Models** was cut. Which model the tokens went to is a cost-engineering concern, not an
 *     org-admin one: no decision available on this panel changes because of it, and it occupied a
 *     card on the landing screen. The per-feature table on Usage answers the question an admin
 *     actually has, which is which FEATURE costs money.
 */

/*
 * "How agentic is the work?" — hidden pending a product decision, and here is what it measures.
 *
 * The figure is `event_count / run_count` per service, from the observability service report
 * (`buildAgentic`): how many ACTIONS the platform took for each run a person started. One prompt
 * that turns into fourteen tool calls scores 14; one that turns into a single completion scores 1.
 * The org figure is total events over total runs rather than the mean of the per-service ratios, so
 * one experimental service with a deep call chain cannot drag the headline.
 *
 * What it is FOR: it is the closest thing on the panel to "how much is actually being delegated",
 * which is the number a buyer renewing an agentic platform asks about. What is WRONG with it as
 * shipped: it is a raw count with no baseline, so a reader has nothing to judge 14 against — higher
 * is not automatically better, and a service that retries a lot scores the same as one doing deep
 * genuine work. That is the decision to make before showing it: name what a good number is, or drop
 * the card.
 *
 * Flagged rather than commented out because commented-out JSX stops type-checking its identifiers,
 * so `agenticRows` and `formatRatio` read as unused and fail the build under `noUnusedLocals` —
 * which is what happened. Same idiom as `SHOW_EDIT_BUDGET` in `MemberUsageTable`.
 */
const SHOW_AGENTIC = false;
export interface UsageSectionProps {
  data: OrgOverview | null;
  stickinessRows: RankedBarDatum[];
  agenticRows: RankedBarDatum[];
  /** Short label for the selected window/month, e.g. "30 days" or "this month". */
  windowLabel: string;
  /** Short label for the stickiness card's comparator, e.g. "7 days" or "last month". */
  recentLabel: string;
  /** Full sentence for the stickiness card — composed by the caller since "the last last month"
      is not a sentence a shared template can produce for both modes. */
  stickinessDescription: string;
  magnitudeColor: string;
  width: number | null;
  isLoading: boolean;
  isFetching: boolean;
}

export function UsageSection({
  data,
  stickinessRows,
  agenticRows,
  windowLabel,
  recentLabel,
  stickinessDescription,
  magnitudeColor,
  width,
  isLoading,
  isFetching,
}: UsageSectionProps) {
  return (
    <Section title="How are they using nCoder?">
      <ChartCard
        title="Repeat use"
        description={stickinessDescription}
        isLoading={isLoading}
        isFetching={isFetching}
        isEmpty={!isLoading && !data?.stickiness.length}
        emptyTitle="No service usage yet"
        emptyDescription="No service recorded a run in this date range."
        testId="stickiness"
        table={
          <ChartValueTable
            caption="Repeat use by service"
            columns={[
              { label: 'Service' },
              { label: `Active in ${recentLabel}`, align: 'right' },
              { label: `Active in ${windowLabel}`, align: 'right' },
              { label: 'Share', align: 'right' },
            ]}
            rows={(data?.stickiness ?? []).map((row) => ({
              key: row.service,
              cells: [
                row.label,
                formatCount(row.recentUsers),
                formatCount(row.windowUsers),
                row.ratio === null ? '—' : `${Math.round(row.ratio * 100)}%`,
              ],
            }))}
          />
        }
      >
        <RankedBarChart
          rows={stickinessRows}
          primary={{ label: 'Repeat use', color: magnitudeColor }}
          formatValue={formatPercent}
          formatAxis={formatPercent}
          // A share cannot exceed 100%; without this the axis pads to 105%.
          domain={[0, 1]}
          containerWidth={width}
        />
      </ChartCard>

      {SHOW_AGENTIC && (
      <div className="mt-3">
        <ChartCard
          title="How agentic is the work?"
          description="Actions the platform took per run — higher means more is being delegated."
          isLoading={isLoading}
          isFetching={isFetching}
          isEmpty={!isLoading && !agenticRows.length}
          emptyTitle="No runs to measure"
          emptyDescription="Actions per run needs at least one completed run."
          skeleton="rows"
          testId="agentic"
          controls={
            data?.agentic.orgAverage !== null && data?.agentic.orgAverage !== undefined ? (
              <p className="oa-meta">
                Org average{' '}
                <span className="font-semibold tabular-nums text-foreground">
                  {formatRatio(data.agentic.orgAverage)}
                </span>
              </p>
            ) : undefined
          }
          table={
            <ChartValueTable
              caption="Actions per run by service"
              columns={[
                { label: 'Service' },
                { label: 'Actions / run', align: 'right' },
                { label: 'Actions', align: 'right' },
                { label: 'Runs', align: 'right' },
              ]}
              rows={(data?.agentic.rows ?? []).map((row) => ({
                key: row.service,
                cells: [
                  row.label,
                  formatRatio(row.eventsPerRun),
                  formatCount(row.events),
                  formatCount(row.runs),
                ],
              }))}
            />
          }
        >
          <RankedBarChart
            rows={agenticRows}
            primary={{ label: 'Actions per run', color: magnitudeColor }}
            formatValue={formatRatio}
            formatAxis={(value) => value.toFixed(0)}
            containerWidth={width}
          />
        </ChartCard>
      </div>
      )}
    </Section>
  );
}
